import type { EnemyKind } from '../ai/archetypes';
import { SeededRng } from '../core/rng';
import type { Rarity } from './rarity';
import type { WearableId } from './wardrobe';

/**
 * Wave L-1 (PLAN-CITY2 §6, Loot 2.0): the UNCOMMON/RARE/EPIC/LEGENDARY layer
 * on top of `loot/droptable.ts`'s existing COMMON coin/material/heart roll
 * (left untouched — those numbers are already tuned per `EnemyKind` and this
 * session has no reason to re-litigate them). Per-source tables (street /
 * elite / boss), a seeded RNG (below), and a real anti-farm cap: a cosmetic
 * is permanently removed from its pool the moment it's owned (an
 * ALREADY-owned wearable can never roll again, full stop — Classic gotcha
 * B6's "a reward hook must have a real per-source cap once real content
 * replaces test data" lesson, applied here as literally as possible: the cap
 * IS the ownership check).
 *
 * RARE ("weapons") deliberately folds into a BIGGER `part` payout rather
 * than a fabricated weapon-unlock system: `combat/weapon.ts`'s own header
 * comment says every weapon in this 3D game ships unlocked from the start
 * (no wallet-gated unlock system exists yet, unlike Classic's 21-weapon
 * economy) — inventing one now, just to have something for "RARE" to grant,
 * would be real scope creep with no player-facing seam to attach it to.
 * Flagged here for Karan rather than silently decided: when this game grows
 * a real weapon-unlock system, RARE should grant a weapon unlock directly
 * instead of a part-payout multiplier.
 */
export type DropSource = 'street' | 'elite' | 'boss';

interface SourceExtrasTable {
  /** UNCOMMON — a small, common part trickle. */
  partChanceUncommon: number;
  partAmountUncommon: number;
  /** RARE, folded into parts per this file's header comment — rarer, bigger. */
  partChanceRare: number;
  partAmountRareMin: number;
  partAmountRareMax: number;
  /** EPIC/LEGENDARY — chance to roll ONE cosmetic from this kill's own pool (see COSMETIC_POOL/BOSS_COSMETIC_POOL), skipped entirely once that pool is fully owned. */
  cosmeticChance: number;
}

const SOURCE_TABLE: Record<DropSource, SourceExtrasTable> = {
  street: { partChanceUncommon: 0.12, partAmountUncommon: 1, partChanceRare: 0.02, partAmountRareMin: 2, partAmountRareMax: 3, cosmeticChance: 0.015 },
  elite: { partChanceUncommon: 0.45, partAmountUncommon: 1, partChanceRare: 0.12, partAmountRareMin: 3, partAmountRareMax: 5, cosmeticChance: 0.1 },
  boss: { partChanceUncommon: 1, partAmountUncommon: 2, partChanceRare: 0.4, partAmountRareMin: 4, partAmountRareMax: 7, cosmeticChance: 0.3 },
};

/**
 * Every current `EnemyKind` that should roll the ordinary UNCOMMON/RARE
 * parts economy maps to exactly one source tier. `golden_pigeon` is
 * DELIBERATELY absent — it never rolls parts (its own `loot/droptable.ts`
 * row already grants a generous coin/material payout, see that file's own
 * comment) but it DOES still get the LEGENDARY plume special-cased directly
 * in `rollEnemyExtras` below, bypassing this table/COSMETIC_POOL entirely
 * (review finding, CONFIRMED HIGH: an earlier version of this comment
 * claimed golden_pigeon's droptable.ts row alone "already IS its reward,"
 * which silently made the documented guaranteed-plume grant unreachable —
 * that row structurally has no cosmetic field at all).
 */
const ENEMY_SOURCE: Partial<Record<EnemyKind, DropSource>> = {
  rat: 'street',
  pizza_rat: 'street',
  trash_lobber: 'street',
  pigeon: 'street',
  dive_pigeon: 'street',
  rat_king: 'elite',
  sewer_elite: 'elite',
};

interface CosmeticEntry {
  id: WearableId;
  /** True only for `rat_king`'s crown and `golden_pigeon`'s plume — a GUARANTEED grant the very first time this kind is ever killed (checked by the caller via a persisted "ever killed" set), not a probability roll. Never re-checked once granted (the ownedWearables filter alone prevents a double-grant if `isFirstKill` is somehow passed true twice). */
  guaranteedOnFirstKill?: boolean;
}

/** Cosmetic pools keyed by `EnemyKind` — `combat/kill.ts`'s `resolveEnemyKill` reads this. Boss cosmetics (Gridlock) are a SEPARATE table below, keyed by boss id, since a `Boss`/`RatKingBoss` isn't an `Enemy` (ai/boss.ts's own header comment). */
const COSMETIC_POOL: Partial<Record<EnemyKind, CosmeticEntry[]>> = {
  rat_king: [
    { id: 'prp_wear_rat_crown', guaranteedOnFirstKill: true },
    { id: 'prp_wear_rat_cape' },
  ],
  rat: [{ id: 'prp_wear_rat_ruff' }],
  pizza_rat: [{ id: 'prp_wear_pizza_slice' }],
  trash_lobber: [{ id: 'prp_wear_trash_lid' }],
  sewer_elite: [{ id: 'prp_wear_manhole_plate' }],
};

/** `bossKey` matches `ai/boss.ts`/`ai/ratking.ts`'s own boss identity — today only `'gridlock'` is ever reachable live (S06); `'rat_king'` is included now so a future Wave D/M swap-over (RatKingBoss going live in a real den) needs no change here, only a new `resolveBossKill('rat_king', ...)` call site. */
export type BossLootKey = 'gridlock' | 'rat_king';

const BOSS_COSMETIC_POOL: Record<BossLootKey, CosmeticEntry[]> = {
  gridlock: [{ id: 'prp_wear_signal_crest' }, { id: 'prp_wear_hubcap_pack' }],
  // Wave D fix session (review finding, confirmed by lens 1 as HIGH): this
  // was `[]` on the theory that "a future Wave D/M swap-over should route
  // rat_king kills through rollEnemyExtras... not this table" — but Wave D
  // landed `resolveBossKill('rat_king', ...)` (main.ts) calling straight into
  // THIS table, so the crown/cape (COSMETIC_POOL.rat_king, the entry that
  // actually has `guaranteedOnFirstKill: true` on the crown) were completely
  // unreachable from the real den kill: every Rat King kill paid 15-25 coins
  // and nothing else, `markKilledOnce('rat_king')` still burned the "ever
  // killed" flag either way, and `isFirstKill` could never be true again —
  // the guaranteed crown could never drop from any source, ever, once a
  // single den kill had happened. Fixed by sharing the SAME array reference
  // (not a copy) with `COSMETIC_POOL.rat_king` — the "not a duplicate pool
  // that could drift" reasoning above is honored literally: there is
  // genuinely only one pool now, just referenced from two lookup tables.
  rat_king: COSMETIC_POOL.rat_king ?? [],
};

const BOSS_COIN_BASE: Record<BossLootKey, { min: number; max: number }> = {
  gridlock: { min: 15, max: 25 },
  rat_king: { min: 15, max: 25 },
};

/**
 * A dedicated seeded source for the UNCOMMON->LEGENDARY layer only (PLAN-
 * CITY2 §6's "seeded-RNG rolls" ask) — deliberately SEPARATE from
 * `loot/droptable.ts`'s existing `Math.random()` calls for coins/hearts,
 * which stay untouched (that file's own comment already gives the reasoned
 * case for per-frame-juice non-determinism, and this session has no reason
 * to revisit it). A permanent-progression reward (a cosmetic, a part cache)
 * benefits from a properly seeded, harness-testable source the way
 * `core/rng.ts`'s own header anticipated for "loot" back at S00 — this game
 * has no restart/replay-determinism requirement to serve (confirmed by
 * droptable.ts's own comment), so the seed value itself is arbitrary; what
 * matters is that it EXISTS and is resettable for tests, not a specific
 * number. `__resetDropsRngForTest` is the same "expose the raw thing for
 * direct testing" convention this codebase already uses elsewhere (e.g.
 * `resolveAimDirectionRaw`).
 */
let dropsRng = new SeededRng(0x8b1d_2f01);

export function __resetDropsRngForTest(seed = 0x8b1d_2f01): void {
  dropsRng = new SeededRng(seed);
}

export interface RolledExtras {
  parts: number;
  /** UNCOMMON/RARE marker for `loot/pickup.ts`'s rarity-tinted beam VFX (L-4) — 'rare' whenever that tier's own roll fired (even if the uncommon roll ALSO fired the same kill; rare wins for display purposes), 'uncommon' if only the smaller roll fired, null if `parts === 0`. */
  partsRarity: Rarity | null;
  cosmetic: WearableId | null;
  /**
   * Wave I (PLAN-CITY3 §5, L6 "rarity claims slots first"): true only when
   * `cosmetic` came from the GUARANTEED-on-first-kill branch below (the Rat
   * King's crown, the Golden Pigeon's plume), never from the probability
   * roll branch. `combat/kill.ts` reads this to decide whether the cosmetic
   * is granted directly to the save (bypassing `PickupPool` entirely — a
   * guaranteed reward must never be at the mercy of a full 64-slot pool or
   * an unreachable drop location) or spawned as an ordinary pickup.
   */
  cosmeticGuaranteed: boolean;
}

function rollPartsAndCosmetic(table: SourceExtrasTable, pool: CosmeticEntry[], ownedWearables: ReadonlySet<WearableId>, isFirstKill: boolean): RolledExtras {
  let parts = 0;
  let partsRarity: Rarity | null = null;
  if (dropsRng.next() < table.partChanceUncommon) {
    parts += table.partAmountUncommon;
    partsRarity = 'uncommon';
  }
  if (dropsRng.next() < table.partChanceRare) {
    parts += dropsRng.int(table.partAmountRareMin, table.partAmountRareMax);
    partsRarity = 'rare';
  }

  let cosmetic: WearableId | null = null;
  let cosmeticGuaranteed = false;
  if (isFirstKill) {
    const guaranteed = pool.find((c) => c.guaranteedOnFirstKill && !ownedWearables.has(c.id));
    if (guaranteed) {
      cosmetic = guaranteed.id;
      cosmeticGuaranteed = true;
    }
  }
  if (!cosmetic) {
    // The "repeat" pool: every entry NOT already owned and not a
    // first-kill-only guarantee (a guaranteed entry that hasn't been earned
    // yet — e.g. `isFirstKill` false but the crown was somehow never
    // granted — is deliberately excluded here too; it only ever comes from
    // the guaranteed branch above, never this probability roll, so a data
    // inconsistency can't be "fixed" by a lucky roll skipping the intended
    // guarantee).
    const candidates = pool.filter((c) => !c.guaranteedOnFirstKill && !ownedWearables.has(c.id));
    if (candidates.length > 0 && dropsRng.next() < table.cosmeticChance) {
      cosmetic = candidates[dropsRng.int(0, candidates.length - 1)].id;
    }
  }

  return { parts, partsRarity, cosmetic, cosmeticGuaranteed };
}

/** golden_pigeon's own one-off LEGENDARY grant — not a `CosmeticEntry`/pool member (it needs no repeat-kill roll, no source-tier parts economy, and no anti-farm logic beyond the same `ownedWearables` check every other cosmetic already gets), so a small standalone constant is clearer than forcing it through machinery built for a different shape. */
const GOLDEN_PIGEON_WEARABLE: WearableId = 'prp_wear_gold_plume';

/**
 * Called from `combat/kill.ts`'s `resolveEnemyKill`, layered ON TOP OF
 * `loot/droptable.ts`'s existing coin/material/heart roll (unchanged).
 * `golden_pigeon` short-circuits BEFORE the normal `ENEMY_SOURCE`/
 * `COSMETIC_POOL` machinery: no parts economy (see `ENEMY_SOURCE`'s own
 * comment), just its own guaranteed-on-first-kill plume.
 */
export function rollEnemyExtras(kind: EnemyKind, ownedWearables: ReadonlySet<WearableId>, isFirstKill: boolean): RolledExtras {
  if (kind === 'golden_pigeon') {
    const cosmetic = isFirstKill && !ownedWearables.has(GOLDEN_PIGEON_WEARABLE) ? GOLDEN_PIGEON_WEARABLE : null;
    return { parts: 0, partsRarity: null, cosmetic, cosmeticGuaranteed: cosmetic !== null };
  }
  const source = ENEMY_SOURCE[kind];
  if (!source) return { parts: 0, partsRarity: null, cosmetic: null, cosmeticGuaranteed: false };
  return rollPartsAndCosmetic(SOURCE_TABLE[source], COSMETIC_POOL[kind] ?? [], ownedWearables, isFirstKill);
}

export interface RolledBossExtras extends RolledExtras {
  coins: number;
}

/**
 * Called from `combat/kill.ts`'s `resolveBossKill` — a boss currently rolls
 * NO loot at all (that function's own header comment flagged this as
 * "Wave C/Part 2 territory"); this is that territory. Coins are granted
 * fresh here (there is no existing boss-side "COMMON layer" to extend the
 * way roster enemies have `loot/droptable.ts`).
 */
export function rollBossExtras(bossKey: BossLootKey, ownedWearables: ReadonlySet<WearableId>, isFirstKill: boolean): RolledBossExtras {
  const coinRange = BOSS_COIN_BASE[bossKey];
  const coins = dropsRng.int(coinRange.min, coinRange.max);
  const extras = rollPartsAndCosmetic(SOURCE_TABLE.boss, BOSS_COSMETIC_POOL[bossKey], ownedWearables, isFirstKill);
  return { coins, ...extras };
}
